import React, { useEffect, useMemo, useState } from "react";
import styles from "./IncomeQualification.module.scss";
import { useTranslation } from "react-i18next";
import InsightAI from "../../../common/InsightAI/InsightAI";
import InteractiveTileGrid from "../InteractiveTileGrid/InteractiveTileGrid";
import { useAppSelector } from "../../../../redux/hooks";
import PersonalPvTile, {
  PersonalPvDetail,
} from "./KPIs/PersonalPv/PersonalPvTile";
import GroupPvTile, { GroupPvDetail } from "./KPIs/GroupPv/GroupPvTile";
import TotalIncomeTile, {
  TotalIncomeDetail,
} from "./KPIs/TotalIncome/TotalIncomeTile";
import CorePlusIncomeTile, {
  CorePlusIncomeDetail,
} from "./KPIs/CorePlusIncome/CorePlusIncomeTile";
import CorePlanIncomeTile, {
  CorePlanIncomeDetail,
} from "./KPIs/CorePlanIncome/CorePlanIncomeTile";
import RetailProfitTile, {
  RetailProfitDetail,
} from "./KPIs/RetailProfit/RetailProfitTile";
import { useGetBusinessPerformanceSummaryQuery } from "../../../../redux/services/businessPerformaceAPI";
import { useProfileParams } from "../../../../utils/common";
import { isEmpty } from "lodash";
import { useGetKpiSettingsQuery } from "../../../../redux/services/kpiSettingsByTypeAPI";

const IncomeQualification: React.FC = () => {
  const { t } = useTranslation();
  const isExpanded = useAppSelector(
    (state: any) => state.sideNavExpansion?.isExpanded
  );
  const { affiliateCode, aboNumber } = useProfileParams();

  const [totalIncomeKPICodeList, setTotalIncomeKPICodeList] = useState<any>([]);

  const { data: totalIncomeKpisResponse } = useGetKpiSettingsQuery(
    {
      type: "business-performance-income-qualification",
      affiliateCode,
    },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    const response = totalIncomeKpisResponse?.body || [];
    const kpiCodes = response.map((item: { code: string }) => ({
      code: item?.code,
    }));

    setTotalIncomeKPICodeList(kpiCodes);
  }, [totalIncomeKpisResponse]);

  const { data: summaryResp, isFetching: isSummaryFetching } =
    useGetBusinessPerformanceSummaryQuery(
      {
        affiliateCode,
        aboNumber,
        payload: { kpis: totalIncomeKPICodeList },
      },
      {
        skip:
          Number(affiliateCode) === 0 ||
          aboNumber === 0 ||
          isEmpty(totalIncomeKPICodeList),
        refetchOnMountOrArgChange: true,
      }
    );

  const fetchedKpis = summaryResp?.body?.kpis ?? [];

  const loading = isSummaryFetching;

  const summaries = useMemo(() => {
    // Extract KPIs from API response
    const retailProfitKpi = fetchedKpis.find(
      (k: any) => k?.kpiCode === "retailProfit"
    );
    const totalIncomeKpi = fetchedKpis.find(
      (k: any) => k?.kpiCode === "totalIncome"
    );
    const corePlusIncomeKpi = fetchedKpis.find(
      (k: any) => k?.kpiCode === "corePlusIncome"
    );
    const corePlanIncomeKpi = fetchedKpis.find(
      (k: any) => k?.kpiCode === "corePlanIncome"
    );
    const personalPvKpi = fetchedKpis.find(
      (k: any) => k?.kpiCode === "personalPv"
    );
    const groupPvKpi = fetchedKpis.find((k: any) => k?.kpiCode === "groupPV");

    return {
      retailProfitKpi,
      totalIncomeKpi,
      corePlusIncomeKpi,
      corePlanIncomeKpi,
      personalPvKpi,
      groupPvKpi,
    };
  }, [fetchedKpis]);

  return (
    <div className={styles.incomeQualification_wrapper}>
      <div className={styles.heading_area}>
        <div className={styles.heading}>{t("income_qualification")}</div>
      </div>

      <InsightAI
        title={"ai_summary_income_qualification"}
        content={""}
        className={styles.insight_ai}
        showDropdown={true}
        isDropdownEnabled={true}
      />

      <div className={styles.tiles_grid_container}>
        <InteractiveTileGrid
          gridColumns={isExpanded ? 3 : 4}
          tiles={[
            {
              id: "personalPv",
              kpiTile: (
                <PersonalPvTile
                  summary={summaries.personalPvKpi}
                  loading={loading}
                />
              ),
              kpiDetailTile: <PersonalPvDetail />,
            },
            {
              id: "groupPv",
              kpiTile: (
                <GroupPvTile summary={summaries.groupPvKpi} loading={loading} />
              ),
              kpiDetailTile: <GroupPvDetail />,
            },
            {
              id: "totalIncome",
              kpiTile: (
                <TotalIncomeTile
                  summary={summaries.totalIncomeKpi}
                  loading={loading}
                />
              ),
              kpiDetailTile: <TotalIncomeDetail />,
            },
            {
              id: "corePlusIncome",
              kpiTile: (
                <CorePlusIncomeTile
                  summary={summaries.corePlusIncomeKpi}
                  loading={loading}
                />
              ),
              kpiDetailTile: <CorePlusIncomeDetail />,
            },
            {
              id: "corePlanIncome",
              kpiTile: (
                <CorePlanIncomeTile
                  summary={summaries.corePlanIncomeKpi}
                  loading={loading}
                />
              ),
              kpiDetailTile: <CorePlanIncomeDetail />,
            },
            {
              id: "retailProfit",
              isVisible: summaries.retailProfitKpi?.kpiValue !== 0,
              kpiTile: (
                <RetailProfitTile
                  summary={summaries.retailProfitKpi}
                  loading={loading}
                />
              ),
              kpiDetailTile: <RetailProfitDetail />,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default IncomeQualification;